import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  EventMessage,
  EventType,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { faBars, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AccountInfoService } from './services/account-info.service';
import { MaintenanceService } from './services/maintenance.service';
import { ToastService } from './services/toast.service';
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: 'app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  faBars = faBars;
  faXmark = faXmark;

  appTitle = 'LexSurvey';
  isIframe = false;
  isLoggedIn = false;
  isMaintenanceMode = false;
  isNavbarVisible = false;
  isAdmin = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private maintenanceService: MaintenanceService,
    private accountInfoService: AccountInfoService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.maintenanceService.getConfig().subscribe((config) => {
      this.isMaintenanceMode = config.isMaintenanceMode;
    });

    this.isIframe = window !== window.parent && !window.opener;

    this.accountInfoService.accountInfo$
      .pipe(takeUntil(this._destroying$))
      .subscribe((accountInfo) => {
        this.isAdmin = accountInfo?.isAdmin ?? false;
      });

    this.msalBroadcastService.inProgress$.subscribe(
      (status: InteractionStatus) => {
        if (
          status === InteractionStatus.None &&
          this.authService.instance.getAllAccounts().length > 0
        ) {
          this.setGlobalUserObj(this.authService.instance.getAllAccounts()[0]);
          // Call any other apis that need to be called after login confirmed here.
        }
      }
    );

    this.setLoginDisplay();

    this.setMsal();
  }

  setMsal() {
    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

    /**
     * You can subscribe to MSAL events as shown below. For more info,
     * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
     */
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result) => {
        this.setLoginDisplay();
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }

  setLoginDisplay() {
    this.isLoggedIn = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getAllAccounts()[0];
    // Set the global user obj.
    this.setGlobalUserObj(activeAccount);
    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      // add your code for handling multiple accounts here
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  login() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.authService
          .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
          .subscribe(() => this.setLoginDisplay());
      } else {
        this.authService.loginPopup().subscribe(() => this.setLoginDisplay());
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.authService.loginRedirect({
          ...this.msalGuardConfig.authRequest,
        } as RedirectRequest);
      } else {
        this.authService.loginRedirect();
      }
    }
  }

  toggleSideNav() {
    this.isNavbarVisible = !this.isNavbarVisible;
  }

  logout() {
    this.authService.logout();
  }

  setGlobalUserObj(account: AccountInfo | null) {
    if (account === null) {
      console.error(account);
      this.toastService.showToast('danger', 'Problem getting account details');
      return;
    }
    this.accountInfoService.setAccountInfo(account);
  }

  // unsubscribe to events when component is destroyed
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
